import React from 'react';
import { ExternalLink, Download, Calendar, MapPin, Phone, Mail, User } from 'lucide-react';

/**
 * Reusable content card component for displaying various content types
 */
export function ContentCard({ 
  title, 
  description, 
  image, 
  date, 
  link, 
  downloadUrl,
  isExternal = false,
  badge = null,
  children
}) {
  const CardWrapper = link ? 'a' : 'div';
  const cardProps = link ? {
    href: link,
    target: isExternal ? '_blank' : undefined,
    rel: isExternal ? 'noopener noreferrer' : undefined
  } : {};

  return (
    <CardWrapper 
      {...cardProps}
      className={`block bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow ${link ? 'cursor-pointer' : ''}`}
    >
      {image && (
        <div className="aspect-video overflow-hidden">
          <img 
            src={image} 
            alt={title} 
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-4 md:p-5">
        {badge && (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-[#0E6D2B] text-white rounded mb-2">
            {badge}
          </span>
        )}
        {date && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
            <Calendar className="w-3 h-3" />
            {date}
          </div>
        )}
        <h3 className="font-semibold text-gray-800 text-sm md:text-base line-clamp-2 mb-2">
          {title}
          {isExternal && <ExternalLink className="inline w-3 h-3 ml-1 text-gray-400" />}
        </h3>
        {description && (
          <p className="text-gray-600 text-xs md:text-sm line-clamp-3">
            {description}
          </p>
        )}
        {downloadUrl && (
          <a 
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-3 text-sm text-[#0E6D2B] hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="w-4 h-4" />
            Download
          </a>
        )}
        {children}
      </div>
    </CardWrapper>
  );
}

/**
 * Staff card component for displaying staff information
 */
export function StaffCard({ name, designation, department, phone, email, image }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      <div className="aspect-square overflow-hidden bg-gray-100">
        {image ? (
          <img src={image} alt={name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-16 h-16 text-gray-300" />
          </div>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 text-sm md:text-base">{name}</h3>
        <p className="text-[#0E6D2B] text-xs md:text-sm font-medium">{designation}</p>
        {department && (
          <p className="text-gray-500 text-xs mt-1">{department}</p>
        )}
        <div className="mt-3 space-y-1">
          {phone && (
            <a href={`tel:${phone}`} className="flex items-center gap-2 text-xs text-gray-600 hover:text-[#0E6D2B]">
              <Phone className="w-3 h-3" />
              {phone}
            </a>
          )}
          {email && (
            <a href={`mailto:${email}`} className="flex items-center gap-2 text-xs text-gray-600 hover:text-[#0E6D2B]">
              <Mail className="w-3 h-3" />
              {email}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Info card for displaying key information/stats
 */
export function InfoCard({ icon: Icon, title, value, description }) {
  return (
    <div className="bg-[#F4F8EF] rounded-lg p-5 text-center">
      {Icon && <Icon className="w-8 h-8 mx-auto text-[#0E6D2B] mb-3" />}
      <div className="text-2xl md:text-3xl font-bold text-[#0E6D2B]">{value}</div>
      <div className="text-sm font-medium text-gray-800 mt-1">{title}</div>
      {description && (
        <p className="text-xs text-gray-600 mt-2">{description}</p>
      )}
    </div>
  );
}

/**
 * Section heading with optional icon
 */
export function SectionHeading({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon className="w-5 h-5 md:w-6 md:h-6 text-[#0E6D2B]" />}
        <h2 className="text-lg md:text-xl font-bold text-[#0E6D2B]">{title}</h2>
      </div>
      {subtitle && (
        <p className="text-gray-600 text-sm">{subtitle}</p>
      )}
    </div>
  );
}

/**
 * Table component for listings
 */
export function DataTable({ columns, data, emptyMessage = "No data available" }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {columns.map((col, index) => (
                <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {columns.map((col, colIndex) => (
                  <td key={colIndex} className="px-4 py-3 text-sm text-gray-700">
                    {col.render ? col.render(row) : row[col.accessor]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
